import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Container,
  Fab,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@material-ui/core";
import Title from "../../ui/Title";
import { Add, MoreVert } from "@material-ui/icons";
import FloatingActions from "../../ui/FloatingActions";
import { useIntl } from "react-intl";
import { common, confirm } from "../../messages";
import { useBmapi } from "../../utils/bmapi-context";
import { getErrorMessageString } from "../../utils/errors";
import { MANAGER_ROUTES } from "../../utils/constants";
import { useHistory } from "react-router-dom";
import Confirm from "../../ui/Confirm";
import { langs } from "../../utils/dictionaries";

function PageElement({ page, handleMenuOpen }) {
  const intl = useIntl();
  const [checked, setChecked] = React.useState(false);

  const pageLang = langs.find((l) => l.value === (page.lang || "it"));

  const handleChange = (event) => {
    setChecked(event.target.checked);
  };

  return (
    <React.Fragment>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            checked={checked}
            onChange={handleChange}
            color="primary"
            inputProps={{ "aria-label": "checkbox selezione elemento" }}
          />
        </TableCell>
        <TableCell>{page.title}</TableCell>
        <TableCell>{page.slug}</TableCell>
        <TableCell>{page.status ? "Pubblicata" : "Non pubblicata"}</TableCell>
        <TableCell>{intl.formatDate(new Date(page.created_at))}</TableCell>
        <TableCell>{intl.formatDate(new Date(page.updated_at))}</TableCell>
        <TableCell>
          {page.status ? intl.formatDate(new Date(page.published_at)) : "-"}
        </TableCell>
        <TableCell>
          <pageLang.Flag
            title={pageLang.label}
            style={{ margin: "5px 0", width: 24 }}
          />
        </TableCell>
        <TableCell align="right">
          <IconButton
            edge="end"
            aria-label="menu"
            onClick={(e) => handleMenuOpen(e, page.id)}
          >
            <MoreVert />
          </IconButton>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

export default function ManageCampusWebBuilder() {
  const intl = useIntl();
  const history = useHistory();
  const { bmapi, notifyError, businessId, notifySuccess } = useBmapi();

  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tablePage, setTablePage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [pages, setPages] = useState([]);
  const [selectedPageId, setSelectedPageId] = useState(null);
  const [deleteRequest, setDeleteRequest] = useState(false);

  useEffect(() => {
    loadPageList();
  }, [bmapi, loadPageList]);

  const handleAddNewPage = () => {
    history.push(MANAGER_ROUTES.WEB_PAGE);
  };

  const handleChangePage = (event, newPage) => {
    setTablePage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setTablePage(0);
  };

  const handleDelete = () => {
    setDeleteRequest(false);

    bmapi
      .deleteWebPage(selectedPageId)
      .then(() =>
        notifySuccess(
          intl.formatMessage({
            id: "pages.webPage.deleted",
            defaultMessage: "Pagina cancellata con successo",
          })
        )
      )
      .then(() => loadPageList())
      .catch((e) => {
        notifyError(getErrorMessageString(e, intl));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDeletePage = () => {
    setDeleteRequest(true);
    setAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuOpen = (event, postId) => {
    setAnchorEl(event.currentTarget);
    setSelectedPageId(postId);
  };

  const loadPageList = useCallback(() => {
    if (bmapi) {
      bmapi
        .getWebPages({
          business: businessId,
        })
        .then((resp) => {
          if (!resp) {
            resp = [];
            setPages(resp);
          } else {
            setPages(resp);
          }
        })
        .catch((e) => {
          notifyError(getErrorMessageString(e, intl));
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [bmapi, intl, notifyError, businessId]);

  return (
    <Container maxWidth="lg">
      <Title>Web builder</Title>
      <Typography variant="h6">Pagine sito web</Typography>
      <Confirm
        open={!!deleteRequest}
        onConfirm={handleDelete}
        onCancel={() => setDeleteRequest(false)}
        text={intl.formatMessage(confirm.deleteElement)}
      />
      <Box mt={2}>
        <Card>
          {loading && <LinearProgress />}
          <CardContent>
            <TableContainer>
              <Table aria-label="sticky table" size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell>Titolo</TableCell>
                    <TableCell>Slug</TableCell>
                    <TableCell>Stato</TableCell>
                    <TableCell>Creazione</TableCell>
                    <TableCell>Ultima modifica</TableCell>
                    <TableCell>Pubblicazione</TableCell>
                    <TableCell>Lingua</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {pages
                    .slice(
                      tablePage * rowsPerPage,
                      tablePage * rowsPerPage + rowsPerPage
                    )
                    .map((p) => {
                      return (
                        <PageElement
                          key={p.id}
                          page={p}
                          handleMenuOpen={handleMenuOpen}
                        />
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={pages.length}
              rowsPerPage={rowsPerPage}
              page={tablePage}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </CardContent>
        </Card>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            history.push(MANAGER_ROUTES.WEB_PAGE + "?id=" + selectedPageId);
          }}
        >
          {intl.formatMessage(common.modify)}
        </MenuItem>
        <MenuItem onClick={handleDeletePage}>
          {intl.formatMessage(common.delete)}
        </MenuItem>
      </Menu>

      <FloatingActions>
        <Fab variant="extended" color="primary" onClick={handleAddNewPage}>
          <Add />
          {intl.formatMessage(common.addPage)}
        </Fab>
      </FloatingActions>
    </Container>
  );
}
